// @flow
import * as React from 'react';
import Modal from 'react-modal';
import { capitalize, debounce } from 'lodash';

import { setAutoMessageStatus, duplicateAutoMessage } from 'admin/actions/AutoMessageActions';
import AdminAutoMessageDetailPreviousSendsTable from './AdminAutoMessageDetailPreviousSendsTable.jsx';
import AdminAutoMessageDetailUnsentFilesTable from './AdminAutoMessageDetailUnsentFilesTable.jsx';
import AdminNewAutoMessageModal from 'admin/components/AdminNewAutoMessage';
import AdminNoticeBanner from 'admin/components/AdminNoticeBanner';
import Button from 'components/Button.jsx';
import Buttons from 'components/Buttons.jsx';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator.jsx';
import Divider from 'components/Divider.jsx';
import ErrorReporter from 'app/components/ErrorReporter.jsx';
import Heading from 'components/Heading.jsx';
import Glyph from 'components/Glyph.jsx';
import LayerTrigger from 'components/LayerTrigger.jsx';
import Link from 'components/Link.jsx';
import MediaObject from 'components/MediaObject.jsx';
import Menu from 'components/Menu.jsx';
import MenuButton from 'components/MenuButton.jsx';
import MenuItem from 'components/MenuItem.jsx';
import Row from 'components/Row.jsx';
import Text from 'components/Text.jsx';
import TextBadge from 'components/TextBadge';
import TextWithGlyph from 'components/TextWithGlyph.jsx';
import Tooltip from 'components/Tooltip.jsx';
import VisibilityWatch from 'components/VisibilityWatch.jsx';

type Props = {
  organizationUUID: string,
  autoMessage: *,
  loading: boolean,
  onRedirect: (messageUuid: string) => void,
  urlSlug: string,
  loadMoreSends: (cursor: string) => *,
};

type State = {
  scrolledToBottom: boolean,
  isNewAutoMessageDialogOpened: boolean,
};

class AdminAutoMessageDetailView extends React.Component<Props, State> {
  state = {
    scrolledToBottom: false,
    isNewAutoMessageDialogOpened: false,
  };

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { autoMessage } = this.props;
    if (!autoMessage) {
      return;
    }

    const lastCursor = autoMessage.previousSends.length
      ? autoMessage.previousSends.edges.slice(-1)[0].cursor
      : null;

    if (!prevState.scrolledToBottom && this.state.scrolledToBottom && lastCursor) {
      this.props.loadMoreSends(lastCursor);
    }
  }

  toggleNewAutoMessageDialog = () => {
    this.setState({ isNewAutoMessageDialogOpened: !this.state.isNewAutoMessageDialogOpened });
  };

  renderHeaderButtons = (canEdit: boolean, isActivatable: boolean) => {
    const { autoMessage, organizationUUID, onRedirect } = this.props;
    const messageIsActive = autoMessage.status === 'active';

    const disabledButton = (
      <LayerTrigger
        noPointer
        layer={
          <Tooltip>
            It looks like not all of the required fields have been filled out yet. Click ‘Edit’ to
            complete and activate the message.
          </Tooltip>
        }>
        <span>
          <Button disabled data-test="activate-button">
            <TextWithGlyph glyph="play">Activate</TextWithGlyph>
          </Button>
        </span>
      </LayerTrigger>
    );
    const menu = (
      <Menu>
        <MenuItem
          data-test="duplicate-action-item"
          onClick={() => duplicateAutoMessage(autoMessage, organizationUUID, onRedirect)}>
          <TextWithGlyph glyph="drag-and-drop">Duplicate</TextWithGlyph>
        </MenuItem>
        <MenuItem>
          <Divider size="flush" />
        </MenuItem>
        <MenuItem>
          <TextWithGlyph glyph="trash">Delete</TextWithGlyph>
        </MenuItem>
      </Menu>
    );

    return (
      <Buttons>
        {isActivatable ? (
          <Button
            data-test="activate-button"
            onClick={() =>
              setAutoMessageStatus(
                autoMessage.uuid,
                messageIsActive ? 'inactive' : 'active',
                organizationUUID,
                autoMessage.messageName
              )
            }>
            <TextWithGlyph glyph={messageIsActive ? 'pause' : 'play'}>
              {messageIsActive ? 'Pause' : 'Activate'}
            </TextWithGlyph>
          </Button>
        ) : (
          disabledButton
        )}
        {canEdit && (
          <Button
            data-test="edit-button"
            level="secondary"
            onClick={this.toggleNewAutoMessageDialog}>
            Edit
          </Button>
        )}
        {canEdit && (
          <MenuButton
            data-test="actions-button"
            menu={menu}
            level="secondary"
            color="koala"
            alignment="right"
            aria-label="More actions">
            <Glyph name="ellipses" size="small" solo />
          </MenuButton>
        )}
      </Buttons>
    );
  };

  renderNotice = (status: 'draft' | 'inactive', csvName: string, unsentFileUploads?: *[]) => {
    const statusToHeading = {
      draft: 'This auto message is a draft',
      inactive: 'This auto message is inactive',
    };

    const copyWithoutReceivedFiles = (
      <Text size="small" lineHeight="large">
        Once Remind receives {csvName || 'a CSV file'}, messages that were not sent will appear
        here. You’ll be able to see who the messages would have been sent to, if this auto message
        had been active.
      </Text>
    );

    return (
      <Row>
        <AdminNoticeBanner
          alwaysShowDetails
          actionElement={undefined}
          color="brand"
          details={
            unsentFileUploads && unsentFileUploads.length ? (
              <AdminAutoMessageDetailUnsentFilesTable unsentFileUploads={unsentFileUploads} />
            ) : (
              copyWithoutReceivedFiles
            )
          }>
          <Heading level="3" flush>
            {statusToHeading[status]}
          </Heading>
        </AdminNoticeBanner>
      </Row>
    );
  };

  setScrolledToBottom = debounce(
    (scrolledToBottom: boolean) => {
      this.setState({ scrolledToBottom });
    },
    200,
    { leading: true, trailing: true }
  );

  renderVisibilityWatcher() {
    return (
      <VisibilityWatch
        whileVisible={() => this.setScrolledToBottom(true)}
        whileInvisible={() => this.setScrolledToBottom(false)}
        interval={500}>
        <div />
      </VisibilityWatch>
    );
  }

  render() {
    const { loading, autoMessage, urlSlug, organizationUUID } = this.props;
    if (!loading && !autoMessage) {
      return (
        <ErrorReporter error="AdminAutoMessageDetailView tried to render without an autoMessage" />
      );
    }

    if (!autoMessage) {
      return <CenteredLoadingIndicator />;
    }

    const {
      unsentFileUploads,
      permissions,
      status,
      messageName,
      isActivatable,
      previousSends,
      csvName,
      uuid,
    } = autoMessage;

    const statusToColor = {
      active: 'shamrock',
      inactive: 'sunrise',
      draft: 'eclipse',
    };

    return (
      <React.Fragment>
        <Modal isOpen={this.state.isNewAutoMessageDialogOpened}>
          <AdminNewAutoMessageModal
            onBack={this.toggleNewAutoMessageDialog}
            organizationUUID={organizationUUID}
            messageUuid={uuid}
          />
        </Modal>
        <Row size="small">
          <Heading level="2">
            <MediaObject figure="right" center>
              <MediaObject center gutter="small">
                <MediaObject center gutter="small">
                  <Link to={urlSlug}>Auto messages</Link>
                  <Glyph size="medium" name="chevron-right" />
                </MediaObject>
                <MediaObject center gutter="small">
                  <Text>{messageName}</Text>
                  <TextBadge textSize="xxxxsmall" textColor="white" color={statusToColor[status]}>
                    {capitalize(status)}
                  </TextBadge>
                </MediaObject>
              </MediaObject>
              {this.renderHeaderButtons(permissions.canEdit, isActivatable)}
            </MediaObject>
          </Heading>
        </Row>
        {!permissions.canEdit && (
          <Row size="small">
            <Text color="eclipse" size="xsmall">
              Only the district admin can edit this auto message.
            </Text>
          </Row>
        )}
        {status !== 'active' && this.renderNotice(status, csvName, unsentFileUploads)}
        <AdminAutoMessageDetailPreviousSendsTable previousSends={previousSends} csvName={csvName} />
        {this.renderVisibilityWatcher()}
      </React.Fragment>
    );
  }
}

export default AdminAutoMessageDetailView;
